import React, { useState } from "react";

function formatMs(ms) {
  if (!ms && ms !== 0) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return ""; }
}

function Connector() {
  return <div className="flow-connector"><div className="flow-line" /><div className="flow-arrow" /></div>;
}

function SubStepBar({ name, durationMs, maxMs, details }) {
  const pct = maxMs > 0 ? Math.max(2, (durationMs / maxMs) * 100) : 2;
  return (
    <div className="pf-substep">
      <span className="pf-substep-name" title={name}>{name}</span>
      <div className="pf-substep-bar-track">
        <div className="pf-substep-bar" style={{ width: `${pct}%` }} />
      </div>
      <span className="pf-substep-time">{formatMs(durationMs)}</span>
      {details && <span className="pf-substep-detail">{Object.entries(details).map(([k, v]) => `${k}:${v}`).join(" ")}</span>}
    </div>
  );
}

function CallChip({ type, call }) {
  const isLlm = type === "llm";
  return (
    <div className={`pf-call-chip ${isLlm ? "pf-call-llm" : "pf-call-di"}`}>
      <span className="pf-call-icon">{isLlm ? "🤖" : "🔍"}</span>
      <span className="pf-call-purpose">{call.purpose || call.model || type}</span>
      <span className="pf-call-meta">
        {isLlm ? `${((call.prompt_tokens || 0) + (call.completion_tokens || 0)).toLocaleString()} tok` : `${call.pages_processed} pg`}
      </span>
      {call.duration_ms > 0 && <span className="pf-call-dur">{formatMs(call.duration_ms)}</span>}
    </div>
  );
}

function ServiceBlock({ title, icon, startedAt, completedAt, totalMs, color, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="pf-component" style={{ borderLeftColor: color }}>
      <div className="pf-component-header" onClick={() => setOpen(!open)}>
        <span className="pf-component-icon">{icon}</span>
        <span className="pf-component-title">{title}</span>
        <span className="pf-component-dur">{formatMs(totalMs)}</span>
        <span className="pf-component-toggle">{open ? "▾" : "▸"}</span>
      </div>
      {startedAt && (
        <div className="pf-component-times">
          <span>Start: {formatTime(startedAt)}</span>
          {completedAt && <span>End: {formatTime(completedAt)}</span>}
        </div>
      )}
      {open && <div className="pf-component-body">{children}</div>}
    </div>
  );
}

function PerformanceTab({ metrics }) {
  if (!metrics) {
    return <div className="mm-empty">No metrics available for this message.</div>;
  }

  // --- Extract per-service documents ---
  const services     = metrics._services || {};
  const mailboxDoc   = services.mailbox    || null;
  const classifierDoc = services.classifier || null;
  const extractorDoc  = services.extractor  || null;
  const sagaDoc       = services.saga       || null;

  // --- Data from each service doc ---
  const classify  = classifierDoc?.classify || metrics.classify || null;
  const extract   = extractorDoc?.extract   || metrics.extract  || null;
  const lossrun   = sagaDoc?.lossrun        || metrics.lossrun  || null;
  const clearance = sagaDoc?.clearance      || metrics.clearance || null;
  const cost      = sagaDoc?.cost           || metrics.cost     || {};

  // Saga orchestration steps
  const sagaSteps     = sagaDoc?.stages || metrics.stages || metrics.steps || [];
  const sagaTotalMs   = sagaDoc?.total_duration_ms || metrics.total_duration_ms || 0;
  const sagaStartedAt = sagaDoc?.initiated_at || sagaDoc?.started_at || metrics.started_at;
  const sagaCompletedAt = sagaDoc?.completed_at || metrics.completed_at;

  // Mailbox
  const mailboxStages = mailboxDoc?.stages || [];
  const mailboxTotalMs     = mailboxDoc?.total_duration_ms || mailboxDoc?.mailbox?.total_ms || 0;
  const mailboxStartedAt   = mailboxDoc?.initiated_at || mailboxDoc?.mailbox?.started_at;
  const mailboxCompletedAt = mailboxDoc?.completed_at  || mailboxDoc?.mailbox?.completed_at;

  // Classifier
  const classifyDiCalls  = classify?.di_calls  || [];
  const classifyLlmCalls = classify?.llm_calls || [];
  const classifySubSteps = classify?.sub_steps || [];
  const classifyTotalMs    = classify?.total_duration_ms || classifierDoc?.classifier_total_duration_ms || 0;
  const classifyStartedAt  = classify?.started_at || classifierDoc?.classifier_started_at;
  const classifyCompletedAt = classify?.completed_at || classifierDoc?.classifier_completed_at;

  // Extractor
  const extractDiCalls  = extract?.di_calls  || [];
  const extractLlmCalls = extract?.llm_calls || [];
  const extractSubSteps = extract?.sub_steps || [];
  const extractTotalMs     = extract?.total_duration_ms || extractorDoc?.extractor_total_duration_ms || 0;
  const extractStartedAt   = extract?.started_at || extractorDoc?.extractor_started_at;
  const extractCompletedAt = extract?.completed_at || extractorDoc?.extractor_completed_at;

  // Total pipeline time: mailbox start → saga end
  let totalMs = 0;
  const pipelineStart = mailboxStartedAt || sagaStartedAt;
  const pipelineEnd   = sagaCompletedAt;
  if (pipelineStart && pipelineEnd) {
    totalMs = new Date(pipelineEnd).getTime() - new Date(pipelineStart).getTime();
  } else {
    totalMs = sagaTotalMs;
  }

  // Max sub-step for bar scaling
  const allSubSteps = [...classifySubSteps, ...extractSubSteps];
  const maxSubMs = Math.max(...allSubSteps.map(s => s.duration_ms || 0), 1);

  return (
    <div className="pf-container">

      {/* ── Summary header ───────────────────────────── */}
      <div className="pf-summary">
        <div className="pf-summary-item">
          <span className="pf-summary-value">{formatMs(totalMs)}</span>
          <span className="pf-summary-label">Total Time</span>
        </div>
        <div className="pf-summary-item">
          <span className="pf-summary-value">${(cost.total_cost_usd || 0).toFixed(4)}</span>
          <span className="pf-summary-label">Total Cost</span>
        </div>
        <div className="pf-summary-item">
          <span className="pf-summary-value">{cost.di_pages_total || 0}</span>
          <span className="pf-summary-label">DI Pages</span>
        </div>
        <div className="pf-summary-item">
          <span className="pf-summary-value">{((cost.llm_prompt_tokens_total || 0) + (cost.llm_completion_tokens_total || 0)).toLocaleString()}</span>
          <span className="pf-summary-label">LLM Tokens</span>
        </div>
      </div>

      {/* ── Saga Engine (outer wrapper) ───────────────── */}
      <ServiceBlock
        title="Saga Engine (Orchestrator)"
        icon="⚙️"
        startedAt={sagaStartedAt}
        completedAt={sagaCompletedAt}
        totalMs={sagaTotalMs}
        color="var(--success)"
      >
        {sagaSteps.length > 0 && (
          <div className="pf-substeps">
            <div className="pf-substeps-title">Orchestration Steps</div>
            {sagaSteps.map((s, i) => (
              <SubStepBar
                key={i}
                name={s.name}
                durationMs={s.duration_ms}
                maxMs={Math.max(...sagaSteps.map(x => x.duration_ms || 0), 1)}
                details={s.status && s.status !== "success" ? { status: s.status } : null}
              />
            ))}
          </div>
        )}

        {/* ── 1. Mailbox ───────────────────────────────── */}
        {mailboxDoc && (
          <>
            <div className="pf-substeps-title" style={{ marginTop: 12 }}>Services Executed</div>
            <ServiceBlock
              title="Mailbox Service"
              icon="📬"
              startedAt={mailboxStartedAt}
              completedAt={mailboxCompletedAt}
              totalMs={mailboxTotalMs}
              color="var(--info)"
            >
              <div className="pf-stats-row">
                {mailboxDoc.attachment_count != null && <span>📎 {mailboxDoc.attachment_count} attachments</span>}
                {mailboxDoc.blob_count != null && <span>☁️ {mailboxDoc.blob_count} blobs</span>}
                {mailboxDoc.is_resync && <span>🔄 Resync</span>}
                {mailboxDoc.is_poller_sync && <span>🔁 Poller</span>}
              </div>
              {mailboxStages.length > 0 && (
                <div className="pf-substeps">
                  <div className="pf-substeps-title">Stage Breakdown</div>
                  {mailboxStages.map((s, i) => (
                    <SubStepBar
                      key={i}
                      name={s.name.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      durationMs={s.duration_ms}
                      maxMs={mailboxTotalMs}
                      details={s.status !== "completed" ? { status: s.status } : null}
                    />
                  ))}
                </div>
              )}
            </ServiceBlock>
            <Connector />
          </>
        )}

        {/* ── 2. Classifier ────────────────────────────── */}
        {classify && (
          <>
            <ServiceBlock
              title="Classification Service"
              icon="🏷️"
              startedAt={classifyStartedAt}
              completedAt={classifyCompletedAt}
              totalMs={classifyTotalMs}
              color="var(--warning)"
            >
              <div className="pf-stats-row">
                <span>📎 {classify.attachments_processed || 0} attachments</span>
                <span>📄 {classify.pdfs_classified || 0} PDFs</span>
                <span>📋 {classify.acord_identified || 0} ACORD</span>
                <span>📊 {classify.sov_identified || 0} SOV</span>
                {classify.lossrun_identified > 0 && <span>📉 {classify.lossrun_identified} Loss Run</span>}
              </div>
              {classifySubSteps.length > 0 && (
                <div className="pf-substeps">
                  <div className="pf-substeps-title">Per-Attachment Processing</div>
                  {classifySubSteps.map((s, i) => (
                    <SubStepBar key={i} name={s.name} durationMs={s.duration_ms} maxMs={maxSubMs} details={s.details} />
                  ))}
                </div>
              )}
              {classifyDiCalls.length > 0 && (
                <div className="pf-calls-section">
                  <div className="pf-calls-title">DI Calls ({classifyDiCalls.length})</div>
                  {classifyDiCalls.map((c, i) => <CallChip key={i} type="di" call={c} />)}
                </div>
              )}
              {classifyLlmCalls.length > 0 && (
                <div className="pf-calls-section">
                  <div className="pf-calls-title">LLM Calls ({classifyLlmCalls.length})</div>
                  {classifyLlmCalls.map((c, i) => <CallChip key={i} type="llm" call={c} />)}
                </div>
              )}
            </ServiceBlock>
            <Connector />
          </>
        )}

        {/* ── 3. Extraction + Lossrun (parallel) ──────── */}
        {(extract || lossrun) && (
          <>
            <div className="pf-parallel">
              <div className="pf-parallel-label">⚡ Parallel Execution</div>
              <div className="pf-parallel-row">

                {/* Extraction */}
                {extract && (
                  <ServiceBlock
                    title="Extraction Service"
                    icon="📤"
                    startedAt={extractStartedAt}
                    completedAt={extractCompletedAt}
                    totalMs={extractTotalMs}
                    color="var(--purple)"
                  >
                    <div className="pf-stats-row">
                      <span>✉️ Mail: {formatMs(extract.mail_extraction_duration_ms)}</span>
                      <span>📋 {extract.acord_documents_processed || 0} ACORD</span>
                      <span>📊 {extract.sov_documents_processed || 0} SOV</span>
                    </div>
                    {extractSubSteps.length > 0 && (
                      <div className="pf-substeps">
                        <div className="pf-substeps-title">Processing Steps</div>
                        {extractSubSteps.map((s, i) => (
                          <SubStepBar key={i} name={s.name.replace("acord_extract_", "ACORD: ")} durationMs={s.duration_ms} maxMs={maxSubMs} details={s.details} />
                        ))}
                      </div>
                    )}
                    {extractDiCalls.length > 0 && (
                      <div className="pf-calls-section">
                        <div className="pf-calls-title">DI Calls ({extractDiCalls.length})</div>
                        {extractDiCalls.map((c, i) => <CallChip key={i} type="di" call={c} />)}
                      </div>
                    )}
                    {extractLlmCalls.length > 0 && (
                      <div className="pf-calls-section">
                        <div className="pf-calls-title">LLM Calls ({extractLlmCalls.length})</div>
                        {extractLlmCalls.map((c, i) => <CallChip key={i} type="llm" call={c} />)}
                      </div>
                    )}
                  </ServiceBlock>
                )}

                {/* Lossrun */}
                {lossrun ? (
                  <ServiceBlock
                    title="Lossrun Service"
                    icon="📋"
                    startedAt={lossrun.started_at}
                    completedAt={lossrun.completed_at}
                    totalMs={lossrun.total_duration_ms}
                    color="var(--info)"
                  >
                    <div className="pf-stats-row">
                      <span>📁 {lossrun.files_count || 0} files sent</span>
                      <span>✅ {lossrun.files_processed || 0} processed</span>
                      <span>🔖 Batch #{lossrun.batch_id || "—"}</span>
                    </div>
                    <div className="pf-stats-row">
                      <span className={`pf-lossrun-status pf-lossrun-${lossrun.status}`}>{lossrun.status || "pending"}</span>
                      {lossrun.total_duration_ms && <span>⏱️ {formatMs(lossrun.total_duration_ms)}</span>}
                    </div>
                    {lossrun.status === "sent" && !lossrun.completed_at && (
                      <div className="pf-lossrun-pending">⏳ Awaiting callback...</div>
                    )}
                  </ServiceBlock>
                ) : extract ? (
                  <div className="pf-component pf-component-skip" style={{ borderLeftColor: "var(--text-dimmed)" }}>
                    <div className="pf-component-header">
                      <span className="pf-component-icon">📋</span>
                      <span className="pf-component-title">Lossrun Service</span>
                      <span className="pf-component-dur">skipped</span>
                    </div>
                    <div className="pf-component-body">
                      <div className="pf-stats-row"><span>No lossrun files identified</span></div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <Connector />
          </>
        )}

        {/* ── 4. Clearance (ASB queue wait) ────────────── */}
        {clearance && (
          <>
            <ServiceBlock
              title="Clearance (ASB Queue)"
              icon="📨"
              startedAt={clearance.published_at}
              completedAt={clearance.received_at}
              totalMs={clearance.duration_ms}
              color="var(--accent)"
            >
              <div className="pf-stats-row">
                <span>📤 Published: {formatTime(clearance.published_at) || "—"}</span>
                <span>📥 Received: {formatTime(clearance.received_at) || "—"}</span>
                {clearance.duration_ms != null && (
                  <span>⏱️ Queue wait: <strong>{formatMs(clearance.duration_ms)}</strong></span>
                )}
              </div>
            </ServiceBlock>
            <Connector />
          </>
        )}

      </ServiceBlock>

      {/* ── End node ─────────────────────────────────── */}
      <div className="pf-end-node">
        <span className="pf-end-icon">✓</span>
        <span className="pf-end-label">Pipeline Complete</span>
        <span className="pf-end-time">{formatMs(totalMs)}</span>
      </div>

    </div>
  );
}

export default PerformanceTab;
